/*
 * NewIndexFieldModal — create a shared index field without leaving the
 * workflow. Opened from a Publish node's "Rename to" list ("+ New index field…").
 *
 * Index field definitions are one shared catalog (iim_fields +
 * iim_field_associations) created in Capture and available in Library, so this
 * calls Capture's own create route with the signed-in user's token — the same
 * route the Field Library page uses:
 *
 *   POST /api/capture/templates/field-library            (field + first doc type)
 *   POST /api/capture/templates/field-library/:key/associations  (each other doc type)
 *
 * applies_to is always 'both': the field is created to be a Library rename
 * target, and the Library only lists fields scoped 'both' or 'library'.
 * Access is the server's builder rule (admin or power_user).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { captureAPI } from '../../services/api';
import { CATEGORY_ORDER, CATEGORY_LABELS } from '../../constants/fieldCategories';
import { VALUE_TYPES } from '../../constants/fieldValueTypes';
import { deriveFieldKey } from './publishFieldSelection';

const EMPTY_KEY_MESSAGE = 'The label needs at least one letter (a-z) or digit to form a field key.';

export default function NewIndexFieldModal({ open, workflowId, onClose, onCreated }) {
  const [label, setLabel] = useState('');
  const [valueType, setValueType] = useState('text');
  const [category, setCategory] = useState('custom');
  const [description, setDescription] = useState('');
  const [docTypes, setDocTypes] = useState([]);
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fresh form each time it opens; doc types from the registry, pre-ticked with
  // the types this workflow's documents have had (none when it has processed none).
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLabel('');
    setValueType('text');
    setCategory('custom');
    setDescription('');
    setChecked([]);
    setError(null);
    setSaving(false);
    Promise.allSettled([
      captureAPI.getDocumentTypes(),
      workflowId ? captureAPI.workflows.getDocumentTypes(workflowId) : Promise.resolve({ documentTypes: [] }),
    ]).then(([registry, seen]) => {
      if (cancelled) return;
      const list = registry.status === 'fulfilled' && Array.isArray(registry.value?.documentTypes)
        ? registry.value.documentTypes : [];
      const options = list
        .filter((t) => t?.key && t.is_active !== false)
        .map((t) => ({ key: t.key, label: t.label || t.key }));
      setDocTypes(options);
      const seenTypes = seen.status === 'fulfilled' && Array.isArray(seen.value?.documentTypes)
        ? seen.value.documentTypes : [];
      const known = new Set(options.map((o) => o.key));
      setChecked(seenTypes.filter((k) => known.has(k)));
    });
    return () => { cancelled = true; };
  }, [open, workflowId]);

  if (!open) return null;

  const derivedKey = deriveFieldKey(label);
  const keyEmpty = label.trim() !== '' && derivedKey === '';

  const toggleDocType = (key) => {
    setChecked((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { setError('Label is required.'); return; }
    if (!derivedKey) { setError(EMPTY_KEY_MESSAGE); return; }
    if (checked.length === 0) { setError('Select at least one document type.'); return; }
    setSaving(true);
    setError(null);
    try {
      const [firstDocType, ...restDocTypes] = checked;
      const resp = await captureAPI.createFieldLibraryEntry({
        label: label.trim(),
        value_type: valueType,
        category,
        description: description.trim() || undefined,
        default_enabled: false,
        applies_to: 'both',
        doc_type: firstDocType,
      });
      const field = resp?.field;
      if (!field?.field_key) {
        setError('Failed to create field.');
        setSaving(false);
        return;
      }
      // The field exists now; a failed extra link does not undo it.
      const failedDocTypes = [];
      for (const dt of restDocTypes) {
        try {
          await captureAPI.addFieldDocTypeAssociation(field.field_key, dt, null);
        } catch {
          failedDocTypes.push(dt);
        }
      }
      setSaving(false);
      onCreated(field, failedDocTypes);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create field.');
      setSaving(false);
    }
  };

  const modal = (
    <div className="ncp-modal-overlay" role="presentation">
      <form
        className="ncp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ncp-new-field-title"
        onSubmit={handleSubmit}
      >
        <div className="ncp-modal-header">
          <h3 id="ncp-new-field-title">New index field</h3>
          <button type="button" className="ncp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ncp-field">
          <label htmlFor="ncp-new-field-label">Label</label>
          <input
            id="ncp-new-field-label"
            className="ncp-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Account Number"
            autoFocus
          />
          <div className={keyEmpty ? 'ncp-error' : 'ncp-hint'} data-testid="ncp-new-field-key">
            {keyEmpty
              ? EMPTY_KEY_MESSAGE
              : derivedKey
                ? `Field key: ${derivedKey} (a number is added if a shared field already uses it)`
                : 'The field key is made from the label.'}
          </div>
        </div>

        <div className="ncp-field">
          <label htmlFor="ncp-new-field-type">Type</label>
          <select id="ncp-new-field-type" className="ncp-select" value={valueType} onChange={(e) => setValueType(e.target.value)}>
            {VALUE_TYPES.map((vt) => <option key={vt} value={vt}>{vt}</option>)}
          </select>
        </div>

        <div className="ncp-field">
          <label htmlFor="ncp-new-field-category">Category</label>
          <select id="ncp-new-field-category" className="ncp-select" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
          </select>
        </div>

        <div className="ncp-field">
          <label htmlFor="ncp-new-field-description">Description</label>
          <input
            id="ncp-new-field-description"
            className="ncp-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="ncp-field">
          <label>Document types (at least one)</label>
          {docTypes.length === 0 ? (
            <div className="ncp-hint">No document types available.</div>
          ) : (
            <div className="ncp-modal-doctypes">
              {docTypes.map(({ key, label: dtLabel }) => (
                <label key={key} className="ncp-checkbox-row">
                  <input
                    type="checkbox"
                    checked={checked.includes(key)}
                    onChange={() => toggleDocType(key)}
                  />
                  {dtLabel}
                </label>
              ))}
            </div>
          )}
        </div>

        {error && <div className="ncp-error" role="alert">{error}</div>}

        <div className="ncp-modal-actions">
          <button type="button" className="ncp-delete-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="submit"
            className="ncp-update-btn"
            disabled={saving || !label.trim() || keyEmpty || checked.length === 0}
          >
            {saving ? 'Creating…' : 'Create field'}
          </button>
        </div>
      </form>
    </div>
  );

  return createPortal(modal, document.body);
}
